import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Home, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      nav("/");
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-[#F8FAFC]">
      {/* Left panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-slate-900 text-white relative overflow-hidden">
        <div className="grid-pattern absolute inset-0 opacity-[0.04]" />
        <div className="relative">
          <div className="flex items-center gap-2.5">
            <div className="w-10 h-10 rounded-lg bg-white grid place-items-center">
              <Home className="w-5 h-5 text-slate-900" strokeWidth={2.5} />
            </div>
            <div className="font-display font-bold text-xl tracking-tight">RoostHQ</div>
          </div>
        </div>
        <div className="relative space-y-6">
          <h1 className="font-display text-5xl font-bold leading-[1.05] tracking-tight">
            Every rand of rent,<br/>
            <span className="text-slate-400">accounted for.</span>
          </h1>
          <p className="text-slate-400 text-base max-w-md leading-relaxed">
            Track monthly rent rolls, tenant contracts, rent increases and payment reminders — all in one calm, minimal workspace.
          </p>
          <div className="flex gap-8 pt-4">
            <div>
              <div className="font-display text-3xl font-bold">R 0.00</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Gross rental</div>
            </div>
            <div>
              <div className="font-display text-3xl font-bold">100%</div>
              <div className="text-xs uppercase tracking-widest text-slate-500 mt-1">Visibility</div>
            </div>
          </div>
        </div>
        <div className="relative text-xs uppercase tracking-widest text-slate-500">
          © RoostHQ — Built for landlords, agents & property owners
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8">
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Welcome back</div>
            <h2 className="font-display text-3xl font-semibold tracking-tight mt-1">Sign in to RoostHQ</h2>
          </div>

          <div className="space-y-4">
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500">Email</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
                className="mt-1.5 w-full h-11 px-3.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-colors duration-200 text-sm"
                placeholder="you@example.com"
              />
            </label>
            <label className="block">
              <span className="text-xs uppercase tracking-widest text-slate-500">Password</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="mt-1.5 w-full h-11 px-3.5 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900 transition-colors duration-200 text-sm"
                placeholder="••••••••"
              />
            </label>
          </div>

          {err && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3.5 py-2.5" data-testid="login-error">{err}</div>}

          <button
            type="submit"
            disabled={loading}
            data-testid="login-submit-btn"
            className="w-full h-11 rounded-lg bg-slate-900 text-white text-sm font-semibold flex items-center justify-center gap-2 hover:bg-slate-800 transition-colors duration-200 disabled:opacity-60"
          >
            {loading ? "Signing in…" : "Sign in"}
            <ArrowRight className="w-4 h-4" />
          </button>

          <div className="text-sm text-slate-500">
            No account?{" "}
            <Link to="/register" className="text-slate-900 font-medium underline underline-offset-4" data-testid="link-register">
              Create one
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
